import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  BoxSizeOptions,
  Inline,
} from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { ProviderServiceGroup } from '../../../../api/Providers/ServiceGroups';
import {
  BodyText,
  Button,
  TabNavigation,
  Touchable,
} from '../../../ui';
import {
  useJanitor,
  useIsMobile,
  useAssignTest,
  useInTest,
  useOnce,
  useAndAssignTest,
} from '../../../../hooks';
import useProfileTabState from './useProfileTabState';
import ConnectedServiceListContainer, { ServiceListContainerProps } from '../ServiceList/ServiceListContainer';

import ReviewListContainer from '../reviews/ReviewListContainer';
import { ProviderProductsListContainer } from '../ProviderProductsList/ProviderProductsListContainer';
import { PROVIDER_PROFILE_PRODUCTS_ENABLED_FLAG } from '../ProviderProductsList/constants';
import analytics from '../../../../modules/analytics';
import FeatureFlags from '../../../../modules/FeatureFlags';
import { selectors as userSelectors } from '../../../../store/CurrentUser.model';
import {
  buildPopularServices,
  buildPopularServicesABTestTopAdjustment,
  buildPreviouslyBookedServices,
} from '../serviceSelectors';
import AboutTab from '../AboutTab';
import {
  DP_273_TEST_REVIEW_ORDERING_NAME,
  DP_273_TEST_REVIEW_ORDERING_PERCENT,
  SEE_ALL_SERVICES_POPUP_TEST_FLAG,
  SEE_ALL_SERVICES_POPUP_TEST_PERCENT,
} from '../ServiceList/constants';
import OnboardingServices from '../onboarding/OnboardingServices';
import { PopularServicesContext, PreviouslyBookedServicesContext } from '../ServicesContext';
import { HighlightedServicesStackProvider } from '../HighlightedServicesSwiper/HighlightedServicesStackProvider';
import EditableBusinessHours from '../BusinessHours/EditableBusinessHours';
import { DeprecatedAboutBusinessHours } from '../BusinessHours';
import EditableCancellationPolicy from '../CancellationPolicy/EditableCancellationPolicy';
import CancellationPolicy from '../CancellationPolicy';
import EditableAboutMe from '../AboutMe/EditableAboutMe';
import AboutMe from '../AboutMe';
import { PublicProviderProfile } from '../../../../modules/Api.types';
import type { IRootDispatch, RootState } from '../../../../store/models';
import { ProviderScheduleDay } from '../../../../store/ProviderWorkSchedule.types';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { RatingsSummary } from '../../../../store/RatingsSummary.model';
import store from '../../../../store';
import { ProviderProfileForReviews, toClientProfile } from '../../../../modules/provider/Profile';
import { isClientProviderProfile } from '../../../../api/ProfileClientView/ProfileClientView';
import { ProviderService } from '../../../../api/Providers/Services';
import { COM_1252_TEST_TOP_SERVICES_ADJUSTMENT_NAME, COM_1252_TEST_TOP_SERVICES_ADJUSTMENT_PERCENT } from '../constants';
import StickyServiceGroupTiles from '../HeaderServices/StickyServiceGroupTiles';
import { AboutProDepositPolicy, EditableDepositPolicy } from '../DepositPolicy';

const CastServiceListContainer = (
  ConnectedServiceListContainer as unknown as React.ComponentType<ServiceListContainerProps>
);

type TabProps = {
  visible: boolean;
  mobileTopPadding?: BoxSizeOptions;
  mobilePadding?: BoxSizeOptions;
};

const Tab: React.FCWithChildren<TabProps> = ({
  visible,
  children,
  mobileTopPadding = 8,
  mobilePadding = 8,
}) => {
  const isMobile = useIsMobile();
  const [rendered, setRendered] = useState(false);

  useOnce(() => {
    setRendered(true);
  }, visible || null);

  if (!rendered) {
    return null;
  }

  return (
    <Box
      style={{ display: visible ? 'flex' : 'none' }}
      color={isMobile ? BrandColor.SuperLight : BrandColor.WhiteWash}
      padding={isMobile ? mobilePadding : 0}
      paddingTop={isMobile ? mobileTopPadding : 0}
    >
      {children}
    </Box>
  );
};

type ProviderProfileTabsProps = {
  profile: PublicProviderProfile | ClientProviderProfile;
  isEditMode?: boolean;
  hasServices?: boolean;
  isOwnProfile?: boolean;
  showCallButton?: boolean;
  shouldShowSpecialties?: boolean;
  hasProviderRecommendationData?: boolean;
  highlightedServiceId?: number;
  maxServiceCount?: number;
  onManageServices?: () => void;
  onOnboardingManageServices?: () => void;
  onMessagePro?: () => void;
  onCallPro?: () => void;
  onShowAllServices?: () => void;
  onManageAvailability?: () => void;
  onEditCancellationPolicy?: () => void;
  onEditDepositPolicy?: () => void;
  onEditPersonalInfo?: () => void;
  onEditBusinessInfo?: () => void;
  showProProfileFilterableTest?: boolean;
  serviceGroups?: ProviderServiceGroup[];
  isServiceListExpanded?: boolean;
};

const ProviderProfileTabs: React.FCWithChildren<ProviderProfileTabsProps> = ({
  profile,
  isEditMode,
  hasServices,
  isOwnProfile,
  showCallButton,
  highlightedServiceId,
  maxServiceCount,
  shouldShowSpecialties,
  hasProviderRecommendationData,
  onManageServices,
  onOnboardingManageServices,
  onMessagePro,
  onCallPro,
  onShowAllServices,
  onManageAvailability,
  onEditCancellationPolicy,
  onEditDepositPolicy,
  onEditPersonalInfo,
  onEditBusinessInfo,
  showProProfileFilterableTest,
  serviceGroups,
  isServiceListExpanded,
}) => {
  const isMobile = useIsMobile();
  const janitor = useJanitor();
  const dispatch = useDispatch<IRootDispatch>();
  const reviewsAreaRef = useRef();
  const products = useSelector<RootState, any>(state => state.providerProducts.list);
  const isLoggedIn = useSelector(userSelectors.isLoggedIn);
  const userId = useSelector(userSelectors.getId);
  const proSchedule = useSelector<RootState, ProviderScheduleDay[]>(
    state => state.providerWorkSchedule.schedules[profile.id],
  );
  const previouslyBookedServices = useSelector<RootState, ProviderService[] | null>(
    state => buildPreviouslyBookedServices(state, profile),
  );
  const popularServices = useSelector<RootState, ProviderService[]>(
    state => buildPopularServices(state, profile),
  );
  const popularServicesABTestTopAdjustment = useSelector<RootState, ProviderService[]>(
    state => buildPopularServicesABTestTopAdjustment(state, profile),
  );
  const popularServiceRankingMap = useSelector<RootState, Record<number, number>>(state => (
    state.providerTopServices?.providerById?.topServiceRankingMap
  ));
  const ratingsSummary = useSelector<RootState, RatingsSummary>(
    store.select.ratingsSummary.forPro({ profileId: profile.id }),
  );
  const profileForReviews = useMemo<ProviderProfileForReviews>(
    () => (
      isClientProviderProfile(profile)
        ? profile
        : toClientProfile(profile, ratingsSummary)
    ),
    [
      profile,
      ratingsSummary,
    ],
  );
  const isInReviewOrderingTest = useAndAssignTest(
    DP_273_TEST_REVIEW_ORDERING_NAME,
    DP_273_TEST_REVIEW_ORDERING_PERCENT,
  );
  const isInSeeAllServicesTest = useInTest(SEE_ALL_SERVICES_POPUP_TEST_FLAG);
  const assignSeeAllServicesTest = useAssignTest(
    SEE_ALL_SERVICES_POPUP_TEST_FLAG,
    SEE_ALL_SERVICES_POPUP_TEST_PERCENT,
  );
  const isInTopServicesAdjustmentTest = useAndAssignTest(
    COM_1252_TEST_TOP_SERVICES_ADJUSTMENT_NAME,
    COM_1252_TEST_TOP_SERVICES_ADJUSTMENT_PERCENT,
  );

  // onMount
  const [isProductsFlagEnabled, setIsProductsFlagEnabled] = useState(false);
  const [hasProductsBeenViewed, setHasProductsBeenViewed] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const usingClientProducts = !isEditMode && (products.length > 0);
  const usingProProducts = isEditMode && isProductsFlagEnabled;
  const usingProducts = usingClientProducts || usingProProducts;

  const {
    tabs,
    activeTabIndex,
    setActiveTabIndex,
    showServices,
    showProducts,
    showReviews,
    showAboutTab,
    isInReviewTagFiltersTest,
  } = useProfileTabState({
    usingProducts,
  });

  useEffect(() => {
    const init = async () => {
      const isProductsEnabled = await janitor.addPromise(
        FeatureFlags.isEnabled(PROVIDER_PROFILE_PRODUCTS_ENABLED_FLAG),
      );

      setIsProductsFlagEnabled(isProductsEnabled);

      if (isProductsEnabled) {
        const loadProducts = dispatch.providerProducts.load;
        janitor.addPromise(loadProducts({ providerId: profile.id }));
      }
    };

    init();
  }, [
    dispatch.providerProducts.load,
    janitor,
    setIsProductsFlagEnabled,
    isEditMode,
    profile,
  ]);
  useEffect(() => {
    if (showAboutTab) {
      setShowMap(true);
    }
  }, [showAboutTab]);

  useEffect(() => {
    if (!isMobile) {
      setShowMap(true);
    }
  }, [isMobile]);
  useEffect(() => {
    if (!isOwnProfile && !maxServiceCount) {
      assignSeeAllServicesTest();
    }
  }, [
    isOwnProfile,
    assignSeeAllServicesTest,
    maxServiceCount,
  ]);

  useEffect(() => {
    if (isLoggedIn && userId && profile?.id) {
      dispatch.previousAppointments.load({
        userId,
        providerId: profile.id,
      });
    }
  }, [
    isLoggedIn,
    userId,
    profile,
    dispatch.previousAppointments,
  ]);

  const handleActiveTabChange = useCallback(
    tabIndex => {
      setActiveTabIndex(tabIndex);
      analytics.track('client_proprofile_tab_clicked', {
        tab_name: tabs[tabIndex],
      });
    },
    [
      tabs,
      setActiveTabIndex,
    ],
  );

  const onProductsMount = () => {
    // Track when clients see products
    if (!hasProductsBeenViewed && !isEditMode) {
      analytics.track('client_affiliate_links_section_viewed', {
        pro_id: profile.id,
        userCreatedProductCount: products.length,
      });
      setHasProductsBeenViewed(true);
    }
  };

  const handleBlurbStatsCounted = React.useCallback(
    ({
      totalBlurbCount,
      emptyBlurbCount,
      defaultedBlurbCount,
    }) => {
      analytics.track('client_proprofile_service_description_stats', {
        total_blurbs: totalBlurbCount,
        num_empty_blurbs: emptyBlurbCount,
        num_defaulted_blurbs: defaultedBlurbCount,
      });
    },
    [],
  );

  const handleProfileHighlightExpanded = ({ title }) => {
    analytics.track('client_proprofile_highlight_expanded', {
      highlight_title: title,
    });
  };

  const handleProfileHighlightCollapsed = ({ title }) => {
    analytics.track('client_proprofile_highlight_collapsed', {
      highlight_title: title,
    });
  };

  const handleProfileMapPressed = () => {
    analytics.track('client_proprofile_map_pressed', {
      provider_id: profile.id,
    });
  };
  return (
    <Box>
      <TabNavigation
        tabs={tabs}
        activeTab={activeTabIndex}
        onSelectedTabChange={handleActiveTabChange}
        paddingX={12}
        paddingTop={8}
      />

      {usingProducts && (
        <Tab visible={showProducts}>
          <ProviderProductsListContainer
            provider={profile}
            isEditMode={isEditMode}
            onMount={onProductsMount}
          />
        </Tab>
      )}

      <Tab
        visible={showServices}
        mobileTopPadding={0}
        mobilePadding={0}
      >
        {hasServices && (
          <PreviouslyBookedServicesContext.Provider value={previouslyBookedServices}>
            <HighlightedServicesStackProvider popularServices={popularServicesABTestTopAdjustment}>
              <PopularServicesContext.Provider value={popularServices}>
                {showProProfileFilterableTest && (
                  <StickyServiceGroupTiles
                    serviceGroups={serviceGroups}
                    onShowAllServices={onShowAllServices}
                    isServiceListExpanded={isServiceListExpanded}
                  />
                )}
                <CastServiceListContainer
                  isViewingEditableProfile={undefined}
                  popularServices={popularServices}
                  provider={profile}
                  isEditMode={isEditMode}
                  isOwnProfile={isOwnProfile}
                  onBlurbStatsCounted={handleBlurbStatsCounted}
                  highlightedServiceId={highlightedServiceId}
                  isInSeeAllServicesTest={
                    isInSeeAllServicesTest
                    && !isOwnProfile
                    && !maxServiceCount
                  }
                  isInTopServicesAdjustmentTest={isInTopServicesAdjustmentTest}
                  maxServiceCount={maxServiceCount}
                  onShowAllServices={onShowAllServices}
                  popularServiceRankingMap={popularServiceRankingMap}
                  hasProviderRecommendationData={hasProviderRecommendationData}
                  isMobile={isMobile}
                  serviceGroups={serviceGroups}
                />
              </PopularServicesContext.Provider>
            </HighlightedServicesStackProvider>
          </PreviouslyBookedServicesContext.Provider>
        )}

        {isEditMode && hasServices && (
          <Button
            size="medium"
            onClick={onManageServices}
            text="Manage Service Menu"
            appearance="primary-neue"
            className="pro-manage-services-button"
          >
            Manage Service Menu
          </Button>
        )}
        {isEditMode && !hasServices && (
          <Box padding={8}>
            <OnboardingServices
              onEdit={onOnboardingManageServices}
            />
          </Box>
        )}
      </Tab>

      <Tab visible={showReviews}>
        <div ref={reviewsAreaRef}>
          <ReviewListContainer
            profile={profileForReviews}
            isEditMode={isEditMode}
            isInReviewTagFiltersTest={isInReviewTagFiltersTest && !isOwnProfile}
            isInReviewOrderingTest={isInReviewOrderingTest}
          />
        </div>
      </Tab>

      <Tab visible={showAboutTab}>
        <AboutTab
          showMap={showMap}
          provider={profile}
          aboutMe={isEditMode ? (
            <EditableAboutMe
              blurb={profile.blurb}
              onEdit={onEditPersonalInfo}
              withHeader
            />
          ) : (
            <AboutMe
              blurb={profile.blurb}
              withHeader
            />
          )}
          locationAction={isEditMode ? (
            <Inline>
              <Touchable
                onClick={onEditBusinessInfo}
                testID="editBusinessInfoLink"
              >
                <Box paddingY={12}>
                  <BodyText
                    appearance="smaller"
                    color={BrandColor.FloraDark}
                    textTransform="uppercase"
                    weight="bold"
                  >
                    Edit Business Info
                  </BodyText>
                </Box>
              </Touchable>
            </Inline>
          ) : null}
          businessHours={isEditMode ? (
            <EditableBusinessHours
              onEdit={onManageAvailability}
              schedule={proSchedule}
            />
          ) : (
            <DeprecatedAboutBusinessHours schedule={proSchedule} />
          )}
          cancellationPolicy={isEditMode ? (
            <EditableCancellationPolicy
              providerId={profile.id}
              onEdit={onEditCancellationPolicy}
            />
          ) : (
            <CancellationPolicy
              cancellationPolicyText={profile.cancellation_policy_text}
              providerName={profile.name}
            />
          )}
          depositPolicy={isEditMode ? (
            <EditableDepositPolicy
              onEdit={onEditDepositPolicy}
            />
          ) : (
            <AboutProDepositPolicy
              depositPolicyText={profile.deposit_policy_text}
              providerName={profile.name}
            />
          )}
          contactActions={isEditMode ? null : (
            <Inline
              space={16}
            >
              <Button
                onClick={onMessagePro}
                appearance="outline-thin"
                size="medium"
                testID="messageButton"
              >
                Message
              </Button>
              {showCallButton && (
                <Button
                  onClick={onCallPro}
                  appearance="outline-thin"
                  size="medium"
                  testID="callButton"
                >
                  Call
                </Button>
              )}
            </Inline>
          )}
          onExpandHighlight={handleProfileHighlightExpanded}
          onCollapseHighlight={handleProfileHighlightCollapsed}
          onPressMap={handleProfileMapPressed}
          shouldShowSpecialties={shouldShowSpecialties && showAboutTab}
        />
      </Tab>
    </Box>
  );
};

ProviderProfileTabs.defaultProps = {
  isEditMode: false,
  isOwnProfile: false,
  hasServices: false,
  showCallButton: false,
  onMessagePro: () => { },
  onCallPro: () => { },
  onOnboardingManageServices: () => { },
  onManageServices: () => { },
  onManageAvailability: () => { },
  onEditBusinessInfo: () => { },
  onEditCancellationPolicy: () => { },
  onEditPersonalInfo: () => { },
  highlightedServiceId: undefined,
  maxServiceCount: null,
  onShowAllServices: () => { },
  shouldShowSpecialties: false,
};

export default ProviderProfileTabs;
